import { useEffect, useState } from 'react'
import type { AppConfigResponse, CatalogFilter, CollectionConfig, Product, ProductsResponse } from '@/types'
import { Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { AdminSection } from './AdminSection'

type Draft = { collectionId: string; startDate: string; endDate: string }
const EMPTY: Draft = { collectionId: '', startDate: '', endDate: '' }

const toDraft = (c: CollectionConfig | null): Draft =>
  c ? { collectionId: c.collectionId, startDate: c.startDate, endDate: c.endDate } : { ...EMPTY }

const toConfig = (d: Draft): CollectionConfig | null =>
  d.collectionId.trim()
    ? { collectionId: d.collectionId.trim(), startDate: d.startDate, endDate: d.endDate }
    : null

function SectionForm({
  title,
  filter,
  draft,
  onChange,
}: {
  title: string
  filter: CatalogFilter
  draft: Draft
  onChange: (d: Draft) => void
}) {
  const [preview, setPreview] = useState<Product[] | null>(null)
  const [checking, setChecking] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  async function verify() {
    const id = draft.collectionId.trim()
    if (!id) return
    setChecking(true)
    setNote(null)
    setPreview(null)
    try {
      const res = await api.get<ProductsResponse>(
        `/products?filter=${filter}&collection=${encodeURIComponent(id)}`
      )
      setPreview(res.products.slice(0, 4))
      if (res.products.length === 0) setNote('La colección no devolvió productos (¿id correcto? ¿stock > 0?).')
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="card space-y-3 p-4">
      <h3 className="font-semibold text-ink">{title}</h3>
      <div>
        <label className="field-label">ID de colección VTEX</label>
        <div className="flex gap-2">
          <input
            className="field-input"
            value={draft.collectionId}
            onChange={(e) => onChange({ ...draft, collectionId: e.target.value })}
            placeholder="ej: 143"
          />
          <button className="btn-secondary shrink-0" onClick={verify} disabled={checking || !draft.collectionId.trim()}>
            {checking ? <Spinner className="h-4 w-4" /> : 'Verificar'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="field-label">Vigencia desde</label>
          <input type="date" className="field-input" value={draft.startDate} onChange={(e) => onChange({ ...draft, startDate: e.target.value })} />
        </div>
        <div>
          <label className="field-label">Hasta</label>
          <input type="date" className="field-input" value={draft.endDate} onChange={(e) => onChange({ ...draft, endDate: e.target.value })} />
        </div>
      </div>
      {note && <p className="text-xs text-warning">{note}</p>}
      {preview && preview.length > 0 && (
        <div>
          <p className="mb-1 text-xs text-ink-muted">Vista previa:</p>
          <div className="no-scrollbar flex gap-2 overflow-x-auto">
            {preview.map((p) => (
              <div key={p.skuId} className="w-16 shrink-0 text-center">
                <div className="aspect-square overflow-hidden rounded-lg bg-surface-sunken">
                  {p.imageUrl && <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <p className="mt-1 line-clamp-2 text-[10px] text-ink-muted">{p.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function AdminCollections() {
  const [hot, setHot] = useState<Draft>(EMPTY)
  const [nw, setNw] = useState<Draft>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api
      .get<AppConfigResponse>('/config')
      .then((res) => {
        setHot(toDraft(res.collections.hot))
        setNw(toDraft(res.collections.new))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function save() {
    setSaving(true)
    setSaved(false)
    try {
      await api.put<{ ok: boolean }>('/config', {
        collections: { hot: toConfig(hot), new: toConfig(nw) },
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminSection title="Hot Items / Novedades">
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 accent-text" />
        </div>
      ) : (
        <div className="space-y-4">
          <SectionForm title="🔥 Hot Items" filter="hot" draft={hot} onChange={setHot} />
          <SectionForm title="✨ Novedades" filter="new" draft={nw} onChange={setNw} />
          <button className="btn-primary btn-block" onClick={save} disabled={saving}>
            {saving ? <Spinner className="h-4 w-4" /> : saved ? '✅ Guardado' : 'Guardar configuración'}
          </button>
          <p className="text-center text-xs text-ink-faint">
            Fuera del rango de vigencia, la pestaña del catálogo muestra estado vacío (RN-02).
          </p>
        </div>
      )}
    </AdminSection>
  )
}
